import type { MongoAbility } from '@casl/ability';

import z from 'zod';

export type AppAbility = MongoAbility<[Actions, Subjects]>;

export const UpdateRbacTagSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
});

export type UpdateRbacTagRequest = z.infer<typeof UpdateRbacTagSchema>;

export enum Actions {
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

export const ADMIN_ACTION = 'manage';

export enum Subjects {
  PROVIDER = 'Provider',
  TEAM_SECRET = 'TeamSecret',
  CONFIG = 'Config',
  PLUGIN_COLLECTION = 'PluginCollection',
  JOB = 'EvalJob',
  EVAL = 'Eval',
  ISSUE = 'Issue',
  GRADING_EXAMPLE = 'GradingExample',
  PLUGIN_SEVERITY_OVERRIDE = 'PluginSeverityOverride',
  GUARDRAIL = 'Guardrail',
  CUSTOM_POLICY = 'CustomPolicy',
  MODEL_AUDIT = 'ModelAudit',
  REPORT = 'Report',
  GRADING_GUIDELINE = 'GradingGuideline',
}

export const SubjectDisplayNames: Record<Subjects, string> = {
  [Subjects.CONFIG]: 'Redteam Configurations',
  [Subjects.EVAL]: 'Evals',
  [Subjects.ISSUE]: 'Vulnerabilities',
  [Subjects.JOB]: 'Scans',
  [Subjects.PLUGIN_COLLECTION]: 'Plugin Collections',
  [Subjects.PROVIDER]: 'Targets',
  [Subjects.TEAM_SECRET]: 'Team Secrets',
  [Subjects.GRADING_EXAMPLE]: 'Grading Examples',
  [Subjects.PLUGIN_SEVERITY_OVERRIDE]: 'Plugin Severity Overrides',
  [Subjects.GUARDRAIL]: 'Guardrails',
  [Subjects.CUSTOM_POLICY]: 'Custom Policies',
  [Subjects.MODEL_AUDIT]: 'Model Audits',
  [Subjects.REPORT]: 'Reports',
  [Subjects.GRADING_GUIDELINE]: 'Grading Guidelines',
};

export enum PermissionSet {
  ADMINISTRATOR = 'ADMINISTRATOR',
  VIEW_CONFIGURATIONS = 'VIEW_CONFIGURATIONS',
  CREATE_CONFIGURATIONS = 'CREATE_CONFIGURATIONS',
  UPDATE_CONFIGURATIONS = 'UPDATE_CONFIGURATIONS',
  DELETE_CONFIGURATIONS = 'DELETE_CONFIGURATIONS',
  MANAGE_CONFIGURATIONS = 'MANAGE_CONFIGURATIONS',
  CREATE_PROVIDERS = 'CREATE_PROVIDERS',
  UPDATE_PROVIDERS = 'UPDATE_PROVIDERS',
  DELETE_PROVIDERS = 'DELETE_PROVIDERS',
  MANAGE_PROVIDERS = 'MANAGE_PROVIDERS',
  RUN_SCANS = 'RUN_SCANS',
  VIEW_EVALS = 'VIEW_EVALS',
  CREATE_EVALS = 'CREATE_EVALS',
  UPDATE_EVALS = 'UPDATE_EVALS',
  DELETE_EVALS = 'DELETE_EVALS',
  MANAGE_EVALS = 'MANAGE_EVALS',
  VIEW_ISSUES = 'VIEW_ISSUES',
  CREATE_ISSUES = 'CREATE_ISSUES',
  UPDATE_ISSUES = 'UPDATE_ISSUES',
  DELETE_ISSUES = 'DELETE_ISSUES',
  MANAGE_ISSUES = 'MANAGE_ISSUES',
  VIEW_PLUGIN_SEVERITY_OVERRIDES = 'VIEW_PLUGIN_SEVERITY_SETS',
  CREATE_PLUGIN_SEVERITY_OVERRIDES = 'CREATE_PLUGIN_SEVERITY_SETS',
  UPDATE_PLUGIN_SEVERITY_OVERRIDES = 'UPDATE_PLUGIN_SEVERITY_SETS',
  DELETE_PLUGIN_SEVERITY_OVERRIDES = 'DELETE_PLUGIN_SEVERITY_SETS',
  MANAGE_PLUGIN_SEVERITY_OVERRIDES = 'MANAGE_PLUGIN_SEVERITY_SETS',
  VIEW_CUSTOM_POLICIES = 'VIEW_CUSTOM_POLICIES',
  CREATE_CUSTOM_POLICIES = 'CREATE_CUSTOM_POLICIES',
  UPDATE_CUSTOM_POLICIES = 'UPDATE_CUSTOM_POLICIES',
  DELETE_CUSTOM_POLICIES = 'DELETE_CUSTOM_POLICIES',
  MANAGE_CUSTOM_POLICIES = 'MANAGE_CUSTOM_POLICIES',
  VIEW_MODEL_AUDITS = 'VIEW_MODEL_AUDITS',
  CREATE_MODEL_AUDITS = 'CREATE_MODEL_AUDITS',
  UPDATE_MODEL_AUDITS = 'UPDATE_MODEL_AUDITS',
  DELETE_MODEL_AUDITS = 'DELETE_MODEL_AUDITS',
  MANAGE_MODEL_AUDITS = 'MANAGE_MODEL_AUDITS',
  VIEW_REPORTS = 'VIEW_REPORTS',
}

export const permissionSetDisplayNames: Record<PermissionSet, string> = {
  [PermissionSet.ADMINISTRATOR]: 'Administrator',
  [PermissionSet.VIEW_CONFIGURATIONS]: 'View Configurations',
  [PermissionSet.CREATE_CONFIGURATIONS]: 'Create Configurations',
  [PermissionSet.UPDATE_CONFIGURATIONS]: 'Update Configurations',
  [PermissionSet.DELETE_CONFIGURATIONS]: 'Delete Configurations',
  [PermissionSet.MANAGE_CONFIGURATIONS]: 'Manage Configurations',
  [PermissionSet.CREATE_PROVIDERS]: 'Create Targets',
  [PermissionSet.UPDATE_PROVIDERS]: 'Update Targets',
  [PermissionSet.DELETE_PROVIDERS]: 'Delete Targets',
  [PermissionSet.MANAGE_PROVIDERS]: 'Manage Targets',
  [PermissionSet.RUN_SCANS]: 'Run Scans',
  [PermissionSet.VIEW_EVALS]: 'View Evaluations',
  [PermissionSet.CREATE_EVALS]: 'Create Evaluations',
  [PermissionSet.UPDATE_EVALS]: 'Update Evaluations',
  [PermissionSet.DELETE_EVALS]: 'Delete Evaluations',
  [PermissionSet.MANAGE_EVALS]: 'Manage Evaluations',
  [PermissionSet.VIEW_ISSUES]: 'View Issues',
  [PermissionSet.CREATE_ISSUES]: 'Create Issues',
  [PermissionSet.UPDATE_ISSUES]: 'Update Issues',
  [PermissionSet.DELETE_ISSUES]: 'Delete Issues',
  [PermissionSet.MANAGE_ISSUES]: 'Manage Issues',
  [PermissionSet.VIEW_PLUGIN_SEVERITY_OVERRIDES]: 'View Plugin Severities',
  [PermissionSet.CREATE_PLUGIN_SEVERITY_OVERRIDES]: 'Create Plugin Severities',
  [PermissionSet.UPDATE_PLUGIN_SEVERITY_OVERRIDES]: 'Update Plugin Severities',
  [PermissionSet.DELETE_PLUGIN_SEVERITY_OVERRIDES]: 'Delete Plugin Severities',
  [PermissionSet.MANAGE_PLUGIN_SEVERITY_OVERRIDES]: 'Manage Plugin Severities',
  [PermissionSet.VIEW_CUSTOM_POLICIES]: 'View Custom Policies',
  [PermissionSet.CREATE_CUSTOM_POLICIES]: 'Create Custom Policies',
  [PermissionSet.UPDATE_CUSTOM_POLICIES]: 'Update Custom Policies',
  [PermissionSet.DELETE_CUSTOM_POLICIES]: 'Delete Custom Policies',
  [PermissionSet.MANAGE_CUSTOM_POLICIES]: 'Manage Custom Policies',
  [PermissionSet.VIEW_MODEL_AUDITS]: 'View Model Audit Scans',
  [PermissionSet.CREATE_MODEL_AUDITS]: 'Create Model Audit Scans',
  [PermissionSet.UPDATE_MODEL_AUDITS]: 'Update Model Audit Scans',
  [PermissionSet.DELETE_MODEL_AUDITS]: 'Delete Model Audit Scans',
  [PermissionSet.MANAGE_MODEL_AUDITS]: 'Manage Model Audit Scans',
  [PermissionSet.VIEW_REPORTS]: 'View Reports',
};

export const permissionSetMappings: Record<PermissionSet, PermissionDTO[]> = {
  [PermissionSet.ADMINISTRATOR]: Object.values(Subjects).flatMap((subject) =>
    Object.values(Actions).map((action) => ({
      subject,
      action,
    })),
  ),
  [PermissionSet.VIEW_CONFIGURATIONS]: [
    { subject: Subjects.CONFIG, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.READ },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.READ },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.READ },
  ],
  [PermissionSet.CREATE_CONFIGURATIONS]: [
    { subject: Subjects.CONFIG, action: Actions.CREATE },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.CREATE },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.CREATE },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.CREATE },
    // Dependencies
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.READ },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.READ },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.READ },
  ],
  [PermissionSet.UPDATE_CONFIGURATIONS]: [
    { subject: Subjects.CONFIG, action: Actions.UPDATE },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.UPDATE },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.UPDATE },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.UPDATE },
    // Dependencies
    { subject: Subjects.CONFIG, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.READ },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.READ },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.READ },
  ],
  [PermissionSet.DELETE_CONFIGURATIONS]: [
    { subject: Subjects.CONFIG, action: Actions.DELETE },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.DELETE },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.DELETE },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.CONFIG, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.READ },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.READ },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.READ },
  ],
  [PermissionSet.MANAGE_CONFIGURATIONS]: [
    // Full CRUD access
    { subject: Subjects.CONFIG, action: Actions.CREATE },
    { subject: Subjects.CONFIG, action: Actions.READ },
    { subject: Subjects.CONFIG, action: Actions.UPDATE },
    { subject: Subjects.CONFIG, action: Actions.DELETE },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.CREATE },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.READ },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.UPDATE },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.DELETE },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.CREATE },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.READ },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.UPDATE },
    { subject: Subjects.GRADING_EXAMPLE, action: Actions.DELETE },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.CREATE },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.READ },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.UPDATE },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
  ],
  [PermissionSet.CREATE_PROVIDERS]: [
    { subject: Subjects.PROVIDER, action: Actions.CREATE },
    // Dependencies
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
    { subject: Subjects.TEAM_SECRET, action: Actions.READ },
  ],
  [PermissionSet.UPDATE_PROVIDERS]: [
    { subject: Subjects.PROVIDER, action: Actions.UPDATE },
    // Dependencies
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
    { subject: Subjects.TEAM_SECRET, action: Actions.READ },
  ],
  [PermissionSet.DELETE_PROVIDERS]: [
    { subject: Subjects.PROVIDER, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
    { subject: Subjects.TEAM_SECRET, action: Actions.READ },
  ],
  [PermissionSet.MANAGE_PROVIDERS]: [
    // Full CRUD access to providers
    { subject: Subjects.PROVIDER, action: Actions.CREATE },
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.UPDATE },
    { subject: Subjects.PROVIDER, action: Actions.DELETE },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
    { subject: Subjects.TEAM_SECRET, action: Actions.READ },
  ],

  [PermissionSet.RUN_SCANS]: [
    { subject: Subjects.JOB, action: Actions.CREATE },
    { subject: Subjects.JOB, action: Actions.READ },
    { subject: Subjects.JOB, action: Actions.UPDATE },
    { subject: Subjects.JOB, action: Actions.DELETE },
    { subject: Subjects.EVAL, action: Actions.READ },
    { subject: Subjects.EVAL, action: Actions.CREATE },
    { subject: Subjects.ISSUE, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
    { subject: Subjects.PLUGIN_COLLECTION, action: Actions.READ },
    { subject: Subjects.CONFIG, action: Actions.READ },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
    { subject: Subjects.GRADING_GUIDELINE, action: Actions.READ },
    { subject: Subjects.TEAM_SECRET, action: Actions.READ },
  ],

  [PermissionSet.VIEW_EVALS]: [
    { subject: Subjects.EVAL, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.CREATE_EVALS]: [
    { subject: Subjects.EVAL, action: Actions.CREATE },
    // Dependencies
    { subject: Subjects.EVAL, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.UPDATE_EVALS]: [
    { subject: Subjects.EVAL, action: Actions.UPDATE },
    // Dependencies
    { subject: Subjects.EVAL, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.DELETE_EVALS]: [
    { subject: Subjects.EVAL, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.EVAL, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.MANAGE_EVALS]: [
    // Full CRUD access to evaluations
    { subject: Subjects.EVAL, action: Actions.CREATE },
    { subject: Subjects.EVAL, action: Actions.READ },
    { subject: Subjects.EVAL, action: Actions.UPDATE },
    { subject: Subjects.EVAL, action: Actions.DELETE },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.VIEW_ISSUES]: [
    { subject: Subjects.ISSUE, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.CREATE_ISSUES]: [
    { subject: Subjects.ISSUE, action: Actions.CREATE },
    // Dependencies
    { subject: Subjects.ISSUE, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.UPDATE_ISSUES]: [
    { subject: Subjects.ISSUE, action: Actions.UPDATE },
    // Dependencies
    { subject: Subjects.ISSUE, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.DELETE_ISSUES]: [
    { subject: Subjects.ISSUE, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.ISSUE, action: Actions.READ },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.MANAGE_ISSUES]: [
    // Full CRUD access to issues
    { subject: Subjects.ISSUE, action: Actions.CREATE },
    { subject: Subjects.ISSUE, action: Actions.READ },
    { subject: Subjects.ISSUE, action: Actions.UPDATE },
    { subject: Subjects.ISSUE, action: Actions.DELETE },
    { subject: Subjects.PROVIDER, action: Actions.READ },
  ],
  [PermissionSet.VIEW_PLUGIN_SEVERITY_OVERRIDES]: [
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
  ],
  [PermissionSet.CREATE_PLUGIN_SEVERITY_OVERRIDES]: [
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.CREATE },
  ],
  [PermissionSet.UPDATE_PLUGIN_SEVERITY_OVERRIDES]: [
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.UPDATE },
  ],
  [PermissionSet.DELETE_PLUGIN_SEVERITY_OVERRIDES]: [
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.DELETE },
  ],
  // Manage: Full CRUD access
  [PermissionSet.MANAGE_PLUGIN_SEVERITY_OVERRIDES]: [
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.CREATE },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.READ },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.UPDATE },
    { subject: Subjects.PLUGIN_SEVERITY_OVERRIDE, action: Actions.DELETE },
  ],
  [PermissionSet.VIEW_CUSTOM_POLICIES]: [{ subject: Subjects.CUSTOM_POLICY, action: Actions.READ }],
  [PermissionSet.CREATE_CUSTOM_POLICIES]: [
    { subject: Subjects.CUSTOM_POLICY, action: Actions.CREATE },
    // Dependencies
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
  ],
  [PermissionSet.UPDATE_CUSTOM_POLICIES]: [
    { subject: Subjects.CUSTOM_POLICY, action: Actions.UPDATE },
    // Dependencies
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
  ],
  [PermissionSet.DELETE_CUSTOM_POLICIES]: [
    { subject: Subjects.CUSTOM_POLICY, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
  ],
  [PermissionSet.MANAGE_CUSTOM_POLICIES]: [
    { subject: Subjects.CUSTOM_POLICY, action: Actions.CREATE },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.READ },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.UPDATE },
    { subject: Subjects.CUSTOM_POLICY, action: Actions.DELETE },
  ],
  [PermissionSet.VIEW_MODEL_AUDITS]: [{ subject: Subjects.MODEL_AUDIT, action: Actions.READ }],
  [PermissionSet.CREATE_MODEL_AUDITS]: [
    { subject: Subjects.MODEL_AUDIT, action: Actions.CREATE },
    // Dependencies
    { subject: Subjects.MODEL_AUDIT, action: Actions.READ },
  ],
  [PermissionSet.UPDATE_MODEL_AUDITS]: [
    { subject: Subjects.MODEL_AUDIT, action: Actions.UPDATE },
    // Dependencies
    { subject: Subjects.MODEL_AUDIT, action: Actions.READ },
  ],
  [PermissionSet.DELETE_MODEL_AUDITS]: [
    { subject: Subjects.MODEL_AUDIT, action: Actions.DELETE },
    // Dependencies
    { subject: Subjects.MODEL_AUDIT, action: Actions.READ },
  ],
  [PermissionSet.MANAGE_MODEL_AUDITS]: [
    // Full CRUD access to model audits
    { subject: Subjects.MODEL_AUDIT, action: Actions.CREATE },
    { subject: Subjects.MODEL_AUDIT, action: Actions.READ },
    { subject: Subjects.MODEL_AUDIT, action: Actions.UPDATE },
    { subject: Subjects.MODEL_AUDIT, action: Actions.DELETE },
  ],
  [PermissionSet.VIEW_REPORTS]: [{ subject: Subjects.REPORT, action: Actions.READ }],
};

export const PermissionSchema = z.object({
  subject: z.nativeEnum(Subjects),
  action: z.nativeEnum(Actions),
});

export type PermissionDTO = z.infer<typeof PermissionSchema>;

// Team Schemas
export const CreateTeamSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  description: z.string().optional(),
  slug: z.string().min(1).optional(),
  probeLimit: z.number().int().nonnegative().nullable().optional(),
});

export const UpdateTeamSchema = z.object({
  name: z.string().min(1, 'Name is required').trim().optional(),
  description: z.string().optional(),
  slug: z.string().min(1).optional(),
  probeLimit: z.number().int().nonnegative().nullable().optional(),
});

export const AddTeamMemberSchema = z.object({
  userId: z.string().uuid('Invalid user ID'),
  roleId: z.string().uuid('Invalid role ID'),
});

export const UpdateTeamMemberSchema = z.object({
  roleId: z.string().uuid('Invalid role ID'),
});

// Role Schemas
export const CreateRoleSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  slug: z.string().min(1).optional(),
  permissionSets: z.array(z.nativeEnum(PermissionSet)),
});

export const UpdateRoleSchema = z.object({
  name: z.string().min(1, 'Name is required').trim(),
  slug: z.string().min(1).optional(),
  permissionSets: z.array(z.nativeEnum(PermissionSet)),
});

export const RoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  permissionSets: z.array(z.nativeEnum(PermissionSet)),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type RoleDTO = z.infer<typeof RoleSchema>;

// Type exports
export type CreateTeamRequest = z.infer<typeof CreateTeamSchema>;
export type UpdateTeamRequest = z.infer<typeof UpdateTeamSchema>;
export type AddTeamMemberRequest = z.infer<typeof AddTeamMemberSchema>;
export type UpdateTeamMemberRequest = z.infer<typeof UpdateTeamMemberSchema>;
export type CreateRoleRequest = z.infer<typeof CreateRoleSchema>;
export type UpdateRoleRequest = z.infer<typeof UpdateRoleSchema>;
